import { AdvancedArticleCategory } from "@/mk2/apps/blogs/schemas";
import { ID_RE, SLUG_LEGACY_RE, USERNAME, url } from "@/mk2/helpers/urls";
import { BlogPostEntity, BlogPostType } from "@/mk2/schemas";

const POST_ID = `:postId(${ID_RE})`;
const COMMENT_ID = `:commentId(${ID_RE})`;
const IMAGE_ID = `:imageId(${ID_RE})`;
const ENTITY_ID = `:entityId(${ID_RE})`;
const GROUP_ID = `:groupId(${ID_RE})`;
const ARTICLE_SLUG = `:articleSlug(${SLUG_LEGACY_RE})`;
const POST_SLUG = `:postSlug(${SLUG_LEGACY_RE})`;

export const RECIPES_SLUG = `recepty`;
export const HOWTOS_SLUG = `navody`;

export const AdvancedArticleTypeSlug = {
    0: "article",
    1: "recept",
    2: "navod",
};

export const DEFAULT_ARTICLE_TYPE_SLUG = "article";
export const RECIPE_ARTICLE_TYPE_SLUG = AdvancedArticleTypeSlug[1];
export const HOWTO_ARTICLE_TYPE_SLUG = AdvancedArticleTypeSlug[2];

const ARTICLE_OR_RECIPE_OR_HOWTO = `:advancedArticleTypeSlug(${DEFAULT_ARTICLE_TYPE_SLUG}|${RECIPE_ARTICLE_TYPE_SLUG}|${HOWTO_ARTICLE_TYPE_SLUG})`;
const RECIPE_OR_HOWTO = `:advancedArticleTypeSlug(${RECIPE_ARTICLE_TYPE_SLUG}|${HOWTO_ARTICLE_TYPE_SLUG})`;
const RECIPES_OR_HOWTOS = `:advancedArticleCategorySlug(${RECIPES_SLUG}|${HOWTOS_SLUG})`;

export const blogsUrl = `/blogs/`;
export const blogsFeedWithCategoryUrl = `/blogs/:category(all|my-activity|pr-articles|flagged|flagged-groups)/`;
export const blogsFriendsFeedUrl = `/blogs/friends/`;
export const blogsPopularFeedUrl = `/blogs/popular/`;
export const blogsFlaggedFeedUrl = `/blogs/flagged/`;
export const blogsFlaggedGroupsFeedUrl = `/blogs/flagged-groups/`;

export const blogsProfileHomeUrl = "/blog/";
export const blogsProfileUrl = `/blog/${USERNAME}/`;
export const blogsProfileMoreUrl = `/blog/${USERNAME}/more/`;
export const blogsProfileProductsUrl = `/blog/${USERNAME}/products/`;
export const blogsProfileEditProUrl = `/blog/${USERNAME}/pro/edit/`;

export const blogsProfileRedirectUrl = `/blog/`;
export const blogsCommentEditUrl = `/blog/comment/${COMMENT_ID}/edit/`;
export const blogsPostUrl = `/blog/${USERNAME}/post/${POST_ID}/`;
export const blogsShortMessageUrl = `/blog/${USERNAME}/message/${POST_SLUG}/`;
export const blogsAutoMessageUrl = `/blog/${USERNAME}/automsg/${POST_SLUG}/`;
export const blogsShareMessageUrl = `/blog/${USERNAME}/share/${POST_SLUG}/`;
export const blogsShortMessageCreateUrl = `/blog/message/${USERNAME}/create/`;
export const blogsShortMessageEditUrl = `/blog/message/${POST_ID}/edit/`;
export const blogsShortMessagePhotoUrl = `/blog/${USERNAME}/message/${POST_SLUG}/${IMAGE_ID}/`;
export const blogsAutoMessagePhotoUrl = `/blog/${USERNAME}/automsg/${POST_SLUG}/${IMAGE_ID}/`;
export const blogsEntityLikersUrl = `/blog/likers/:type(short-message|album|article|comment)/${ENTITY_ID}/`;
export const blogsPostShareUrl = `/blog/post/${POST_ID}/share/`;

export const blogsArticleCreateUrl = `/blog/${USERNAME}/${DEFAULT_ARTICLE_TYPE_SLUG}/create/`;
export const blogsArticleEditUrl = `/blog/${USERNAME}/${DEFAULT_ARTICLE_TYPE_SLUG}/${ARTICLE_SLUG}/edit/`;

export const blogsAdvancedArticleCreateUrl = `/blog/${USERNAME}/${RECIPE_OR_HOWTO}/create/`; // for example. /blog/username/recept/create/
export const blogsAdvancedArticleEditUrl = `/blog/${USERNAME}/${RECIPE_OR_HOWTO}/${ARTICLE_SLUG}/edit/`;

export const blogsArticleUrl = `/blog/${USERNAME}/${DEFAULT_ARTICLE_TYPE_SLUG}/${ARTICLE_SLUG}/`;
export const blogsArticleDraftsUrl = `/blog/${USERNAME}/${ARTICLE_OR_RECIPE_OR_HOWTO}/drafts/`;

export const blogsAdvancedArticleUrl = `/blog/${USERNAME}/${RECIPE_OR_HOWTO}/${ARTICLE_SLUG}/`;

export const blogsRecipeArticleCreateUrl = `/blog/${USERNAME}/${RECIPE_ARTICLE_TYPE_SLUG}/create/`;
export const blogsRecipeArticleEditUrl = `/blog/${USERNAME}/${RECIPE_ARTICLE_TYPE_SLUG}/${ARTICLE_SLUG}/edit/`;
export const blogsRecipeArticlesUrl = `/blog/${USERNAME}/${RECIPES_SLUG}/`;

export const blogsHowtoArticleCreateUrl = `/blog/${USERNAME}/${HOWTO_ARTICLE_TYPE_SLUG}/create/`;
export const blogsHowtoArticleEditUrl = `/blog/${USERNAME}/${HOWTO_ARTICLE_TYPE_SLUG}/${ARTICLE_SLUG}/edit/`;
export const blogsHowtoArticlesUrl = `/blog/${USERNAME}/${HOWTOS_SLUG}/`;

// export const blogsAdvancedArticlePreviewGridUrl = '' +
//     `/${RECIPES_OR_HOWTOS}` +
//     `/:dirtyUsername(u\/${USERNAME_RE})?` +
//     `/:dirtySubCategory(c\/${SLUG_LEGACY_RE})?` +
//     `/:dirtyHashtag(h\/${SLUG_LEGACY_RE})?` +
//     `/:draft(drafts)?` +
//     `/`;

export const blogsAdvancedArticlePreviewGridUrl =
    "" +
    `/${RECIPES_OR_HOWTOS}` +
    `/:flag1(u|c|h|s)?` +
    `/:value1(${SLUG_LEGACY_RE})?` +
    `/:flag2(u|c|h|s)?` +
    `/:value2(${SLUG_LEGACY_RE})?` +
    `/:flag3(u|c|h|s)?` +
    `/:value3(${SLUG_LEGACY_RE})?` +
    `/:flag4(u|c|h|s)?` +
    `/:value4(${SLUG_LEGACY_RE})?` +
    `/`;
export const recipesHomepageUrl = `/${RECIPES_SLUG}/`;
export const howtoHomepageUrl = `/${HOWTOS_SLUG}/`;

// Group advanced article URLs (recipes/how-tos in groups)
export const groupAdvancedArticleUrl = `/group/${GROUP_ID}/${RECIPE_OR_HOWTO}/${POST_SLUG}/`;

export const blogsAlbumDetailUrl = `/blog/${USERNAME}/album/${POST_SLUG}/`;
export const blogsAlbumPhotoUrl = `/blog/${USERNAME}/album/${POST_SLUG}/${IMAGE_ID}/`;
export const blogsAlbumReorderUrl = `/blog/${USERNAME}/album/${POST_ID}/reorder/`;
export const blogsAlbumCreateUrl = `/blog/${USERNAME}/album/create/`;
export const blogsAlbumEditUrl = `/blog/${USERNAME}/album/${POST_ID}/edit/`;
export const blogsAlbumEditPhotosUrl = `/blog/${USERNAME}/album/${POST_ID}/add/`;

export const advancedArticleUrl = (post: BlogPostEntity) => {
    const username = post.user?.username || post.author?.username;
    const articleSlug = post.slug;
    const categoryId = post.advancedArticle?.category?.id;

    // Check if this is a group advanced article
    if (post.group?.id && post.advancedArticle) {
        const advancedArticleTypeSlug =
            categoryId === AdvancedArticleCategory.RECIPES
                ? RECIPE_ARTICLE_TYPE_SLUG
                : HOWTO_ARTICLE_TYPE_SLUG;
        return url(groupAdvancedArticleUrl, {
            groupId: post.group.id,
            advancedArticleTypeSlug,
            postSlug: articleSlug,
        });
    }

    if (categoryId === AdvancedArticleCategory.RECIPES) {
        return url(blogsAdvancedArticleUrl, {
            username,
            advancedArticleTypeSlug: RECIPE_ARTICLE_TYPE_SLUG,
            articleSlug,
        });
    } else if (categoryId === AdvancedArticleCategory.HOWTO) {
        return url(blogsAdvancedArticleUrl, {
            username,
            advancedArticleTypeSlug: HOWTO_ARTICLE_TYPE_SLUG,
            articleSlug,
        });
    } else {
        return url(blogsArticleUrl, { username, articleSlug });
    }
};

export const editArticleBodyUrl = (post: BlogPostEntity) => {
    const username = post.user?.username || post.author?.username;
    const articleSlug = post.slug;
    return url(blogsArticleEditUrl, { username, articleSlug });
};

export const editAdvancedArticleDetailsUrl = (post: BlogPostEntity) => {
    const username = post.user?.username || post.author?.username;
    const articleSlug = post.slug;
    const categoryId = post.advancedArticle?.category?.id;
    if (categoryId === AdvancedArticleCategory.RECIPES) {
        return url(blogsRecipeArticleEditUrl, { username, articleSlug });
    } else if (categoryId === AdvancedArticleCategory.HOWTO) {
        return url(blogsHowtoArticleEditUrl, { username, articleSlug });
    } else {
        return url(blogsArticleEditUrl, { username, articleSlug });
    }
};

export const blogsPostDetailUrl = (postType: BlogPostType, imageId?: number) => {
    /*  */ if (postType === BlogPostType.ALBUM && imageId) {
        return blogsAlbumPhotoUrl;
    } else if (postType === BlogPostType.ALBUM && !imageId) {
        return blogsAlbumDetailUrl;
    } else if (postType === BlogPostType.ARTICLE) {
        return blogsArticleUrl.replace(ARTICLE_SLUG, POST_SLUG);
    } else if (postType === BlogPostType.AUTOMATIC_MESSAGE && imageId) {
        return blogsAutoMessagePhotoUrl;
    } else if (postType === BlogPostType.AUTOMATIC_MESSAGE && !imageId) {
        return blogsAutoMessageUrl;
    } else if (postType === BlogPostType.SHARE) {
        return blogsShareMessageUrl;
    } else if (imageId) {
        return blogsShortMessagePhotoUrl;
    } else {
        return blogsShortMessageUrl;
    }
};

export const blogsPostEditUrl = (post: BlogPostEntity) => {
    if (post.type === BlogPostType.ALBUM) {
        return blogsAlbumEditUrl;
    } else if (post.type === BlogPostType.ARTICLE) {
        return blogsArticleEditUrl.replace(ARTICLE_SLUG, POST_SLUG);
    } else if (post.type === BlogPostType.SHORT_MESSAGE) {
        return blogsShortMessageEditUrl;
    } else {
        return null; // other types are actually not allowed to be edited
    }
};
